import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePlans } from '../../../../marketing-v2/features/plans/usePlans'
import PlanCard from '../../../../marketing-v2/features/plans/components/PlanCard'
import { dismissPlan, undismissPlan } from '../../../../marketing-v2/features/plans/data/planStore'
import { planPath } from '../../../../marketing-v2/features/campaigns/data/open'
import { familyForLook } from '../../../../marketing-v2/features/social/render/families'
import { hapticTick } from '../../../../../_utils/haptics'
import { useBrand } from '../useBeu'
import { Photo, Tick } from './icons'
import { useFacts } from '../saveBus'
import { addPlanEdits, currentPlan, planFacts, editsSaid } from '../planEdits'

export const MARKETING_HOME_PATH = '/marketing-home'

// Which plans a "focus" means (the plan kinds in plans/data/planner.js)
const FOCUS = {
  fill: ['fill', 'quiet'],
  season: ['moment'],
  review: ['review'],
  team: ['intro'],
  winback: ['winback'],
}

// Bailey's plans, in the chat (2026-10-06). The same ready-made campaigns as
// Marketing home (the season, quiet days, a review, a new face, win-back,
// each with its text, email, posts and Stories in the salon's brand, its
// goal first), so Bailey connects what Beu already has instead of making
// posts from scratch. Nothing sends from here: "Review and approve" opens
// the campaign in the studio, with the usual audience, credit and approval
// checks, exactly as from Marketing home.
const PlansWidget = ({ block, id, compact, go, latest = false }) => {
  const dispatch = useDispatch()
  const user = useSelector(s => (s && s.user) || null)
  const brand = useBrand()
  const hairdresser = (user && user.userInfo && user.userInfo.hairdresser) || {}
  const userSalon = hairdresser.salon || {}
  const country = String(userSalon.countryShortName || userSalon.country || 'GB').toUpperCase().slice(0, 2)
  const { loading, plans } = usePlans({
    dispatch,
    user,
    trade: brand.market,
    salonName: brand.salonName,
    brandColour: brand.kit.accent,
    family: familyForLook(brand.spec.look),
    country,
    links: brand.links,
    currency: hairdresser.currencySymbol || '£',
    enabled: brand.ready,
  })
  const [hidden, setHidden] = useState({})
  // A block that changes one plan ("drop the text"): its edits are kept with
  // the plan once, so later blocks and the studio start from the changed plan
  const [rev, setRev] = useState(0)
  useEffect(() => {
    if (block.plan && block.edits && block.edits.length) { addPlanEdits(block.plan, block.edits, id); setRev(n => n + 1) }
  }, [block.plan, id]) // eslint-disable-line react-hooks/exhaustive-deps
  const shown = useMemo(() => {
    const kinds = block.focus ? FOCUS[block.focus] : null
    // A playbook block names one scenario: that card alone
    if (block.key) return plans.filter(p => p.scenario && p.scenario.key === block.key && !hidden[p.key]).slice(0, 1)
    return plans
      .filter(p => !hidden[p.key])
      .filter(p => (block.plan ? p.key === block.plan : (!kinds || kinds.includes(p.kind))))
      .slice(0, block.plan ? 1 : (block.max || (compact ? 2 : 3)))
      .map(currentPlan)
  }, [plans, hidden, block.focus, block.max, block.plan, block.key, compact, rev]) // eslint-disable-line react-hooks/exhaustive-deps
  // Bailey sees what's on screen, so "move the Story to Thursday" can name it
  useFacts(id, latest && shown.length > 0, () => planFacts(shown))
  const changed = block.plan && block.edits && shown[0] ? editsSaid(shown[0], block.edits) : []

  const review = (plan) => { hapticTick(); go(planPath(plan), { from: '/assistant' }) }
  const dismiss = (plan, reason) => {
    dismissPlan(plan.key, reason)
    setHidden(h => ({ ...h, [plan.key]: true }))
  }
  const undo = (key) => { undismissPlan(key); setHidden(h => ({ ...h, [key]: false })) }

  if (loading || !brand.ready) {
    return (
      <div className="bw-card is-loading" role="status" aria-label="Getting Bailey’s plans">
        <div className="bw-head"><span className="bw-kind"><Photo />{block.title || 'This week’s plans'}</span></div>
        <p className="bw-line">Reading your diary, reviews, team and the dates coming up</p>
        <span className="bw-bar bw-shimmer" aria-hidden />
        <span className="bw-bar is-short bw-shimmer" aria-hidden />
      </div>
    )
  }
  const putAway = Object.keys(hidden).filter(k => hidden[k])
  return (
    <div className="bw-card is-wide bw-plans" role="group" aria-label={block.title || 'This week’s plans'}>
      <div className="bw-head">
        <span className="bw-kind"><Photo />{block.title || (block.plan ? 'Your plan, changed' : block.key ? 'Your plan' : 'This week’s plans')}</span>
        {changed.length > 0 && <span className="bw-status is-done"><Tick size={12} />{changed.join(', ')}</span>}
      </div>
      {block.key && shown.length === 0 ? (
        <p className="bw-line">That one isn’t on offer any more (it may have been done or put away). Ask Bailey what to do next.</p>
      ) : block.plan && shown.length === 0 ? (
        <p className="bw-line">That plan isn’t on offer any more (it may have been approved or put away). Ask Bailey for this week’s plans.</p>
      ) : shown.length === 0 ? (
        <p className="bw-line">Nothing stands out right now. Your diary, the dates coming up and your reviews are all in hand.</p>
      ) : (
        <div className="bw-plans-list">
          {shown.map(p => (
            <PlanCard
              key={p.key}
              plan={p}
              kit={brand.kit}
              salonName={brand.salonName}
              onReview={review}
              onDismiss={dismiss}
              option={block.option}
              onAction={(x) => { hapticTick(); go(x.action.path, { ...(x.action.state || {}), from: '/assistant' }) }}
            />
          ))}
        </div>
      )}
      {putAway.length > 0 && (
        <p className="bw-note">Put away. <button type="button" className="bw-link" onClick={() => putAway.forEach(undo)}>Undo</button></p>
      )}
      <div className="bw-actions">
        <button type="button" className="bw-btn is-secondary" onClick={() => { hapticTick(); go(MARKETING_HOME_PATH) }}>All plans in Marketing</button>
      </div>
    </div>
  )
}

export default PlansWidget
